import { bridge } from "@peerup/helper"
import { isEqual } from "lodash-es"

/**
 * 从账户相关链接中提取唯一ID
 * 支持 /accounts/application/:id 或 /accounts/invitation/:id 等格式
 * @param url 完整的URL字符串
 * @returns 提取到的ID字符串，未匹配返回空字符串
 * @example
 * extractAccountIdFromUrl('http://localhost:9400/accounts/application/LUX2eTvBtYDpIagOliz6') // 'LUX2eTvBtYDpIagOliz6'
 * extractAccountIdFromUrl('http://localhost:9400/accounts/invitation/LUX2eTvBtYDpIagOliz6') // 'LUX2eTvBtYDpIagOliz6'
 */
import { IS_DEV } from "@/config"
import router from "@/router"
import { useUserStore } from "@/store/modules/user"

import $logger from "./logger"

export function extractAccountIdFromUrl(url: string): string {
  // 只匹配 /accounts/application/:id 或 /accounts/invitation/:id
  const match = url.match(/\/accounts\/(?:application|invitation)\/([\w-]+)/)
  return match ? match[1] : url
}

/**
 * 跳转到指定路由，并自动合并当前 query 参数
 * @param path 目标路由 path
 * @param extraQuery 额外需要添加的 query 参数
 */
export const redirectWithQuery = (path: string, extraQuery: Record<string, unknown> = {}) => {
  const userStore = useUserStore()
  const currentQuery = router.currentRoute.value.query
  const currentPath = router.currentRoute.value.path
  const isSamePath = path.replace(/^\//, "") === currentPath.replace(/^\//, "")
  const targetQuery = { ...currentQuery, ...extraQuery } as Record<string, string | string[]>
  const isSameQuery = isEqual(currentQuery, targetQuery)
  // 如果路径和参数都相同，无需跳转
  if (isSamePath && isSameQuery) {
    return
  }
  console.warn("redirectWithQuery", { path, extraQuery, currentQuery, currentPath, isSamePath, isSameQuery })

  if (isSamePath && !isSameQuery) {
    try {
      userStore.updateState({ ...extraQuery })
      router.replace({
        path,
        query: targetQuery as Record<string, string | string[]>,
      })
    } catch (error) {
      console.error(error)
    }
    return
  }

  // 使用 replace 或 push 进行路由跳转
  const routerMethod = isSamePath ? router.replace : router.push
  routerMethod({
    path,
    query: targetQuery as Record<string, string | string[]>,
  })
}
export const getBaseOrigin = () => {
  return IS_DEV ? `http://${window.location.hostname}:5173` : window.location.origin
}

export const getDirectUrlInQuery = () => {
  const urlSearchParams = new URLSearchParams(window.location.search)
  return decodeURIComponent(urlSearchParams.get("redirect") || "")
}

/**
 * 获取重定向URL
 * @param query 额外需要添加的 query 参数
 * @returns 重定向URL
 */
export const getRedirectUrl = (query: Record<string, string> = {}) => {
  const redirectUrl = getDirectUrlInQuery() || "/home"
  // 判断环境，拼接 base
  const baseOrigin = getBaseOrigin()
  const urlObj = new URL(
    redirectUrl.startsWith("http")
      ? redirectUrl
      : baseOrigin + redirectUrl,
  )

  Object.entries(query).forEach(([k, v]) => {
    urlObj.searchParams.set(k, v)
  })
  return urlObj.toString()
}

export const jumpWithRedirect = (
  query: Record<string, string>,
  targetInNewTab = false,
) => {
  bridge.closeSelf()

  const url = getRedirectUrl(query)

  if (targetInNewTab) {
    window.open(url)
  } else {
    $logger.log("离开页面", { url }, { immediate: true })
    window.location.href = url
  }
}
